import React, { useState, useRef, useEffect } from "react";
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeHighlight from 'rehype-highlight';
import 'highlight.js/styles/github.css'; // Import highlight.js CSS
import { sendChatMessageStream, sendChatMessage, ChatMessage, AVAILABLE_MODELS } from "../api/llm-ws.ts";

const LLMChat = () => {
    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const [inputValue, setInputValue] = useState("");
    const [selectedModel, setSelectedModel] = useState<string>('qwen2.5:7b-instruct-q8_0');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [useStreaming, setUseStreaming] = useState(true);
    const [copiedText, setCopiedText] = useState<string | null>(null);
    const [mode, setMode] = useState<'llm' | 'rag' | 'agent'>('llm');
    const [uploadedImages, setUploadedImages] = useState<{file: File, base64: string, preview: string}[]>([]);
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Helper function to check if current model supports images
    const currentModelSupportsImages = () => {
        const model = AVAILABLE_MODELS.find(m => m.id === selectedModel);
        return model?.supportsImages || false;
    };

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ 
            behavior: "smooth",
            block: "end",
            inline: "nearest"
        });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    // Clear uploaded images when switching to a non-vision model
    useEffect(() => {
        if (!currentModelSupportsImages() && uploadedImages.length > 0) {
            // Revoke object URLs to prevent memory leaks
            uploadedImages.forEach(img => URL.revokeObjectURL(img.preview));
            setUploadedImages([]);
        }
    }, [selectedModel, uploadedImages.length]);

    // Enhanced preprocessing to fix markdown issues including tables
    const preprocessContent = (content: string): string => {
        let processed = content;
        
        // First, detect if this looks like a table and preserve it during other processing
        const hasTableStructure = /\|.*\|/.test(processed) && processed.includes('---');
        
        if (!hasTableStructure) {
            // Only apply general formatting if it's not a table
            processed = processed
                // Add line breaks after sentences that should start new paragraphs
                .replace(/\.\s*([A-Z][a-z])/g, '.\n\n$1')
                // Add line breaks after colons when followed by capital letters (for section starts)
                .replace(/:\s*([A-Z][a-z])/g, ':\n\n$1')
                // Add line breaks before bullet points that aren't already on new lines
                .replace(/([^-\n])-\s*([A-Z])/g, '$1\n- $2');
        }
        
        // Table-specific fixes
        processed = processed
            // Fix table headers that might be missing proper separators
            .replace(/(\|[^|\n]+\|)\s*\n\s*(\|[^|\n]+\|)/g, (match, header, row) => {
                // Check if there's a separator row between header and data
                if (!row.includes('---') && !processed.includes('---')) {
                    // Add a separator row
                    const headerCols = (header.match(/\|/g) || []).length - 1;
                    const separator = '|' + ' --- |'.repeat(headerCols);
                    return header + '\n' + separator + '\n' + row;
                }
                return match;
            })
            // Fix incomplete table rows (ensure they end with |)
            .replace(/\|([^|\n]*[^|\s\n])(?=\n)/g, '|$1|')
            // Fix table cells that might have extra spaces
            .replace(/\|\s+([^|]*?)\s+\|/g, '| $1 |')
            // Clean up excessive newlines but preserve table structure
            .replace(/\n{3,}/g, '\n\n');
            
        return processed.trim();
    };

    // Helper function to convert file to base64
    const fileToBase64 = (file: File): Promise<string> => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => {
                const result = reader.result as string;
                // Remove the data:image/xxx;base64, prefix to get just the base64 string
                const base64 = result.split(',')[1];
                console.log('Base64 length:', base64?.length, 'Preview:', base64?.substring(0, 50) + '...');
                resolve(base64);
            };
            reader.onerror = error => reject(error);
        });
    };

    // Helper function to create image preview URL
    const createImagePreview = (file: File): string => {
        return URL.createObjectURL(file);
    };

    const handleSendMessage = async () => {
        if (!inputValue.trim() || isLoading) return;

        const userMessage: ChatMessage = {
            role: 'user',
            content: inputValue.trim() || '',
            timestamp: new Date(),
            images: uploadedImages.length > 0 ? uploadedImages.map(img => img.base64) : undefined
        };

        // Add user message to chat
        setMessages(prev => [...prev, userMessage]);
        const currentMessage = inputValue.trim() || '';
        const currentModel = selectedModel;
        const currentImages = uploadedImages.map(img => img.base64);
        console.log('Sending images:', currentImages.length, 'First image length:', currentImages[0]?.length, 'Preview:', currentImages[0]?.substring(0, 50) + '...');
        setInputValue("");
        setUploadedImages([]);
        setIsLoading(true);
        setError(null);

        // Create a placeholder assistant message for streaming
        const assistantMessageId = Date.now();
        const assistantMessage: ChatMessage = {
            role: 'assistant',
            content: '',
            timestamp: new Date(),
            model: currentModel
        };
        
        setMessages(prev => [...prev, assistantMessage]);

        if (useStreaming) {
            // Use streaming API
            sendChatMessageStream(
                currentMessage,
                currentModel,
                // onMessage: Update the streaming content
                (chunk: string) => {
                    setMessages(prev => {
                        const newMessages = [...prev];
                        const lastMessage = newMessages[newMessages.length - 1];
                        if (lastMessage && lastMessage.role === 'assistant') {
                            // Create a new message object instead of mutating
                            newMessages[newMessages.length - 1] = {
                                ...lastMessage,
                                content: lastMessage.content + chunk
                            };
                        }
                        return newMessages;
                    });
                },
                // onComplete: Streaming finished
                () => {
                    setIsLoading(false);
                },
                // onError: Handle errors
                (errorMessage: string) => {
                    setError(errorMessage);
                    setMessages(prev => {
                        const newMessages = [...prev];
                        const lastMessage = newMessages[newMessages.length - 1];
                        if (lastMessage && lastMessage.role === 'assistant' && lastMessage.content === '') {
                            // Create a new message object instead of mutating
                            newMessages[newMessages.length - 1] = {
                                ...lastMessage,
                                content: `Error: ${errorMessage}`
                            };
                        }
                        return newMessages;
                    });
                    setIsLoading(false);
                },
                mode,
                currentImages
            );
        } else {
            // Use non-streaming API for complete response
            try {
                const response = await sendChatMessage(currentMessage, currentModel, mode, currentImages);
                
                // Update the placeholder message with the complete response
                setMessages(prev => {
                    const newMessages = [...prev];
                    const lastMessage = newMessages[newMessages.length - 1];
                    if (lastMessage && lastMessage.role === 'assistant') {
                        // Create a new message object instead of mutating
                        newMessages[newMessages.length - 1] = {
                            ...lastMessage,
                            content: response
                        };
                    }
                    return newMessages;
                });
            } catch (err) {
                const errorMessage = err instanceof Error ? err.message : 'An unknown error occurred';
                setError(errorMessage);
                
                setMessages(prev => {
                    const newMessages = [...prev];
                    const lastMessage = newMessages[newMessages.length - 1];
                    if (lastMessage && lastMessage.role === 'assistant') {
                        // Create a new message object instead of mutating
                        newMessages[newMessages.length - 1] = {
                            ...lastMessage,
                            content: `Error: ${errorMessage}`
                        };
                    }
                    return newMessages;
                });
            } finally {
                setIsLoading(false);
            }
        }
    };

    const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        }
    };

    const formatTimestamp = (timestamp: Date) => {
        return timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };

    const getModelDisplayName = (modelId?: string) => {
        if (!modelId) return '';
        const model = AVAILABLE_MODELS.find(m => m.id === modelId);
        return model ? model.name : modelId;
    };

    const copyToClipboard = (text: string) => {
        navigator.clipboard.writeText(text).then(() => {
            setCopiedText(text);
            setTimeout(() => setCopiedText(null), 2000); // Clear after 2 seconds
        }).catch(err => {
            console.error('Failed to copy: ', err);
        });
    };

    const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        // Check if current model supports images
        if (!currentModelSupportsImages()) {
            setError('Please select a vision-capable model (Qwen VL models).');
            if (event.target) {
                event.target.value = '';
            }
            return;
        }

        const files = event.target.files;
        if (files) {
            const newFiles = Array.from(files);
            // Validate image file types and sizes
            const validFiles = newFiles.filter(file => {
                const maxSize = 5 * 1024 * 1024; // 5MB limit for images
                const allowedTypes = [
                    'image/jpeg',
                    'image/jpg',
                    'image/png',
                    'image/gif',
                    'image/webp',
                    'image/bmp',
                    'image/svg+xml'
                ];
                
                if (file.size > maxSize) {
                    setError(`Image "${file.name}" is too large. Maximum size is 5MB.`);
                    return false;
                }
                
                if (!allowedTypes.includes(file.type)) {
                    setError(`File type "${file.type}" is not supported. Please upload images only (JPEG, PNG, GIF, WebP, BMP, SVG).`);
                    return false;
                }
                
                return true;
            });
            
            // Convert images to base64 and create preview URLs
            for (const file of validFiles) {
                try {
                    const base64 = await fileToBase64(file);
                    const preview = createImagePreview(file);
                    setUploadedImages(prev => [...prev, { file, base64, preview }]);
                } catch (err) {
                    setError(`Failed to process image "${file.name}"`);
                }
            }
            setError(null);
        }
        
        // Reset input value to allow re-uploading the same file
        if (event.target) {
            event.target.value = '';
        }
    };

    const removeImage = (index: number) => {
        setUploadedImages(prev => {
            const newImages = [...prev];
            // Revoke the object URL to prevent memory leaks
            URL.revokeObjectURL(newImages[index].preview);
            return newImages.filter((_, i) => i !== index);
        });
    };

    const formatFileSize = (bytes: number): string => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDrop = async (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        
        // Check if current model supports images
        if (!currentModelSupportsImages()) {
            setError('Please select a vision-capable model (Qwen VL models).');
            return;
        }
        
        const files = Array.from(e.dataTransfer.files);
        if (files.length > 0) {
            // Validate and add images using the same logic as file input
            const validFiles = files.filter(file => {
                const maxSize = 5 * 1024 * 1024; // 5MB for images
                const allowedTypes = [
                    'image/jpeg',
                    'image/jpg',
                    'image/png',
                    'image/gif',
                    'image/webp',
                    'image/bmp',
                    'image/svg+xml'
                ];
                
                if (file.size > maxSize) {
                    setError(`Image "${file.name}" is too large. Maximum size is 5MB.`);
                    return false;
                }
                
                if (!allowedTypes.includes(file.type)) {
                    setError(`File type "${file.type}" is not supported. Please upload images only (JPEG, PNG, GIF, WebP, BMP, SVG).`);
                    return false;
                }
                
                return true;
            });
            
            // Convert images to base64 and create preview URLs
            for (const file of validFiles) {
                try {
                    const base64 = await fileToBase64(file);
                    const preview = createImagePreview(file);
                    setUploadedImages(prev => [...prev, { file, base64, preview }]);
                } catch (err) {
                    setError(`Failed to process image "${file.name}"`);
                }
            }
            setError(null);
        }
    };

    return (
        <div 
            className="flex flex-col overflow-hidden bg-gray-50" 
            style={{ height: 'calc(100vh - 6rem)' }}
            onDragOver={handleDragOver}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
        >
            {/* Header - Fixed at top */}
            <div className="flex-shrink-0 px-4 py-3 bg-white border-b shadow-sm">
                <div className="flex items-center justify-between">
                    <div>
                        <h2 className="text-lg font-semibold text-gray-800">AI Chat Assistant</h2>
                        <p className="text-sm text-gray-600">
                            Mode: <span className="font-medium text-blue-600">{mode.toUpperCase()}</span> • Ask me anything!
                        </p>
                    </div>
                    <div className="flex items-center space-x-4">
                        {/* Mode Selector */}
                        <div className="flex items-center space-x-2">
                            <label htmlFor="mode-select" className="text-sm font-medium text-gray-700">
                                Mode:
                            </label>
                            <select
                                id="mode-select"
                                value={mode}
                                onChange={(e) => setMode(e.target.value as 'llm' | 'rag' | 'agent')}
                                className="px-3 py-1 text-sm bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                disabled={isLoading}
                                title="Select chat mode"
                            >
                                <option value="llm">LLM</option>
                                <option value="rag">RAG</option>
                                <option value="agent">Agent</option>
                            </select>
                        </div>
                        
                        <div className="flex items-center space-x-2">
                            <label htmlFor="model-select" className="text-sm font-medium text-gray-700">
                                Model:
                            </label>
                            <select
                                id="model-select"
                                value={selectedModel}
                                onChange={(e) => setSelectedModel(e.target.value)}
                                className="px-3 py-1 text-sm bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent min-w-[200px]"
                                disabled={isLoading}
                                title="Select AI model for responses"
                            >
                                {/* Group models by category */}
                                {Object.entries(
                                    AVAILABLE_MODELS.reduce((acc, model) => {
                                        if (!acc[model.category]) acc[model.category] = [];
                                        acc[model.category].push(model);
                                        return acc;
                                    }, {} as Record<string, typeof AVAILABLE_MODELS[number][]>)
                                ).map(([category, models]) => (
                                    <optgroup key={category} label={category}>
                                        {models.map((model) => (
                                            <option key={model.id} value={model.id}>
                                                {model.name}
                                            </option>
                                        ))}
                                    </optgroup>
                                ))}
                            </select>
                        </div>
                        
                        {/* Streaming Toggle */}
                        <div className="flex items-center space-x-2">
                            <label htmlFor="streaming-toggle" className="text-sm font-medium text-gray-700">
                                Streaming:
                            </label>
                            <button
                                id="streaming-toggle"
                                onClick={() => setUseStreaming(!useStreaming)}
                                disabled={isLoading}
                                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                                    useStreaming ? 'bg-blue-600' : 'bg-gray-200'
                                }`}
                                title="Toggle streaming mode"
                            >
                                <span
                                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                        useStreaming ? 'translate-x-6' : 'translate-x-1'
                                    }`}
                                />
                            </button>
                            <span className="text-xs text-gray-500">
                                {useStreaming ? 'On' : 'Off'}
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Messages Container - Scrollable area that takes remaining space */}
            <div className="flex-1 min-h-0 overflow-hidden">
                <div className="h-full p-4 space-y-4 overflow-y-auto">
                    {messages.length === 0 ? (
                    <div className="mt-8 text-center text-gray-500">
                        <p className="mb-2 text-lg">👋 Hello!</p>
                        <p className="mb-1">Start a conversation by typing a message below.</p>
                        <p className="mb-2 text-sm text-gray-400">
                            Mode: {mode.toUpperCase()} • Using: {getModelDisplayName(selectedModel)} • Change mode/model in the header dropdowns
                        </p>
                        <p className="text-xs text-gray-400">
                            ✨ Supports Markdown formatting, code blocks, tables, and more!
                        </p>
                    </div>
                ) : (
                    messages.map((message, index) => (
                        <div
                            key={index}
                            className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                        >
                            <div
                                className={`max-w-2xl lg:max-w-3xl px-4 py-3 rounded-lg ${
                                    message.role === 'user'
                                        ? 'bg-blue-500 text-white'
                                        : message.content.startsWith('Error:')
                                        ? 'bg-red-100 text-red-800 border border-red-200'
                                        : 'bg-white text-gray-800 border border-gray-200'
                                }`}
                            >
                                {message.role === 'user' ? (
                                    // User messages as plain text
                                    <div>
                                        <p className="text-sm whitespace-pre-wrap">
                                            {message.content}
                                        </p>
                                        {message.images && message.images.length > 0 && (
                                            <div className="pt-2 mt-2 border-t border-blue-400">
                                                <div className="flex items-center mb-2 space-x-1">
                                                    <svg className="w-3 h-3 text-blue-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                                    </svg>
                                                    <span className="text-xs text-blue-200">Attached images ({message.images.length}):</span>
                                                </div>
                                                <div className="text-xs italic text-blue-200">
                                                    Images were processed with this message
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    // Assistant messages with Markdown rendering
                                    <div className="prose-sm prose max-w-none prose-gray prose-headings:font-medium prose-h1:text-lg prose-h1:font-medium prose-h2:text-base prose-h2:font-medium prose-h3:text-sm prose-h3:font-medium prose-p:text-sm prose-p:font-normal prose-p:leading-relaxed prose-li:text-sm prose-li:font-normal prose-li:leading-relaxed prose-code:text-xs prose-pre:text-xs prose-blockquote:text-sm prose-strong:font-semibold">
                                        <ReactMarkdown
                                            remarkPlugins={[remarkGfm]}
                                            rehypePlugins={[rehypeHighlight]}
                                            components={{
                                                // Custom styling for code blocks
                                                pre: ({ children, ...props }) => {
                                                    const codeContent = children?.toString() || '';
                                                    return (
                                                        <div className="relative group">
                                                            <button
                                                                onClick={() => copyToClipboard(codeContent)}
                                                                className="absolute z-10 px-2 py-1 text-xs text-white transition-opacity bg-gray-600 rounded opacity-0 top-2 right-2 group-hover:opacity-100 hover:bg-gray-500"
                                                                title="Copy to clipboard"
                                                            >
                                                                {copiedText === codeContent ? '✓ Copied' : 'Copy'}
                                                            </button>
                                                            <pre 
                                                                {...props} 
                                                                className="markdown-pre"
                                                                style={{ whiteSpace: 'pre-wrap', wordWrap: 'break-word' }}
                                                            >
                                                                {children}
                                                            </pre>
                                                        </div>
                                                    );
                                                },
                                                // Enhanced inline code
                                                code: ({ children, className, ...props }) => {
                                                    const isInline = !className || !className.includes('language-');
                                                    if (isInline) {
                                                        return (
                                                            <code 
                                                                {...props} 
                                                                className="markdown-code-inline"
                                                            >
                                                                {children}
                                                            </code>
                                                        );
                                                    }
                                                    return <code {...props} className={className}>{children}</code>;
                                                },
                                                // Enhanced table styling
                                                table: ({ children, ...props }) => (
                                                    <div className="my-4 overflow-x-auto">
                                                        <table {...props} className="markdown-table">
                                                            {children}
                                                        </table>
                                                    </div>
                                                ),
                                                th: ({ children, ...props }) => (
                                                    <th {...props} className="markdown-table th">
                                                        {children}
                                                    </th>
                                                ),
                                                td: ({ children, ...props }) => (
                                                    <td {...props} className="markdown-table td">
                                                        {children}
                                                    </td>
                                                ),
                                                // Enhanced blockquotes
                                                blockquote: ({ children, ...props }) => (
                                                    <blockquote {...props} className="markdown-blockquote">
                                                        {children}
                                                    </blockquote>
                                                ),
                                                // Enhanced lists
                                                ul: ({ children, ...props }) => (
                                                    <ul {...props} className="list-disc list-inside markdown-list">
                                                        {children}
                                                    </ul>
                                                ),
                                                ol: ({ children, ...props }) => (
                                                    <ol {...props} className="list-decimal list-inside markdown-list">
                                                        {children}
                                                    </ol>
                                                ),
                                                li: ({ children, ...props }) => (
                                                    <li {...props} className="markdown-list-item">
                                                        {children}
                                                    </li>
                                                ),
                                                // Enhanced strong/bold text
                                                strong: ({ children, ...props }) => (
                                                    <strong {...props} className="markdown-strong">
                                                        {children}
                                                    </strong>
                                                ),
                                                // Enhanced emphasis/italic text
                                                em: ({ children, ...props }) => (
                                                    <em {...props} className="markdown-em">
                                                        {children}
                                                    </em>
                                                ),
                                                // Enhanced links
                                                a: ({ children, ...props }) => (
                                                    <a {...props} className="markdown-link" target="_blank" rel="noopener noreferrer">
                                                        {children}
                                                    </a>
                                                ),
                                                // Enhanced headings
                                                h1: ({ children, ...props }) => (
                                                    <h1 {...props} className="markdown-h1">
                                                        {children}
                                                    </h1>
                                                ),
                                                h2: ({ children, ...props }) => (
                                                    <h2 {...props} className="markdown-h2">
                                                        {children}
                                                    </h2>
                                                ),
                                                h3: ({ children, ...props }) => (
                                                    <h3 {...props} className="markdown-h3">
                                                        {children}
                                                    </h3>
                                                ),
                                                // Enhanced paragraphs
                                                p: ({ children, ...props }) => (
                                                    <p {...props} className="markdown-p">
                                                        {children}
                                                    </p>
                                                ),
                                                // Enhanced horizontal rules
                                                hr: ({ ...props }) => (
                                                    <hr {...props} className="markdown-hr" />
                                                ),
                                            }}
                                        >
                                            {preprocessContent(message.content || '')}
                                        </ReactMarkdown>
                                        {/* Show blinking cursor for streaming assistant messages */}
                                        {isLoading && index === messages.length - 1 && (
                                            <span className="inline-block w-2 h-4 ml-1 bg-gray-600 animate-pulse"></span>
                                        )}
                                    </div>
                                )}
                                <div className={`text-xs mt-1 flex items-center justify-between ${
                                    message.role === 'user' 
                                        ? 'text-blue-100' 
                                        : 'text-gray-500'
                                }`}>
                                    <span>{formatTimestamp(message.timestamp)}</span>
                                    {message.role === 'assistant' && message.model && (
                                        <span className="ml-2 px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs">
                                            {getModelDisplayName(message.model)}
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))
                )}
                
                {/* Loading indicator */}
                {isLoading && (
                    <div className="flex justify-start">
                        <div className="max-w-xs px-4 py-2 text-gray-800 bg-white border border-gray-200 rounded-lg lg:max-w-md">
                            <div className="flex items-center space-x-2">
                                <div className="flex space-x-1">
                                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-pulse"></div>
                                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-pulse" style={{ animationDelay: '0.1s' }}></div>
                                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-pulse" style={{ animationDelay: '0.2s' }}></div>
                                </div>
                                <div className="flex flex-col">
                                    <span className="text-sm text-gray-500">AI is responding...</span>
                                    <span className="text-xs text-gray-400">{getModelDisplayName(selectedModel)}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
                    
                    <div ref={messagesEndRef} />
                </div>
            </div>

            {/* Error display - Fixed position */}
            {error && (
                <div className="flex-shrink-0 p-3 mx-4 mb-2 text-red-700 bg-red-100 border border-red-300 rounded-lg">
                    <p className="text-sm">{error}</p>
                </div>
            )}

            {/* Input Section - Fixed at bottom */}
            <div className="flex-shrink-0 p-4 bg-white border-t">
                {/* Image Upload Preview */}
                {uploadedImages.length > 0 && (
                    <div className="p-3 mb-3 border rounded-lg bg-gray-50">
                        <div className="flex items-center justify-between mb-3">
                            <h4 className="text-sm font-medium text-gray-700">Attached Images ({uploadedImages.length})</h4>
                            <button
                                onClick={() => setUploadedImages([])}
                                className="text-xs text-red-600 transition-colors hover:text-red-800"
                                title="Remove all images"
                            >
                                Clear All
                            </button>
                        </div>
                        <div className="flex flex-wrap gap-2">
                            {uploadedImages.map((imageData, index) => (
                                <div key={index} className="relative group bg-white rounded-lg border shadow-sm p-2 max-w-[120px]">
                                    {/* Image Preview */}
                                    <div className="flex flex-col items-center text-center">
                                        <img 
                                            src={imageData.preview} 
                                            alt={imageData.file.name}
                                            className="object-cover w-16 h-16 mb-2 rounded"
                                        />
                                        {/* File Name */}
                                        <p className="w-full mb-1 text-xs font-medium text-gray-900 truncate" title={imageData.file.name}>
                                            {imageData.file.name.length > 12 ? imageData.file.name.substring(0, 9) + '...' : imageData.file.name}
                                        </p>
                                        {/* File Size */}
                                        <p className="text-xs text-gray-500">{formatFileSize(imageData.file.size)}</p>
                                    </div>
                                    
                                    {/* Remove Button */}
                                    <button
                                        onClick={() => removeImage(index)}
                                        className="absolute p-1 text-white transition-opacity bg-red-500 rounded-full shadow-sm opacity-0 -top-1 -right-1 group-hover:opacity-100 hover:bg-red-600"
                                        title="Remove image"
                                    >
                                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
                
                <div className="flex space-x-2">
                    {/* Image Upload Button */}
                    <input
                        ref={fileInputRef}
                        type="file"
                        multiple
                        onChange={handleFileUpload}
                        className="hidden"
                        accept="image/*,.jpg,.jpeg,.png,.gif,.webp,.bmp,.svg"
                        disabled={isLoading || !currentModelSupportsImages()}
                    />
                    <button
                        onClick={() => currentModelSupportsImages() ? fileInputRef.current?.click() : null}
                        disabled={isLoading || !currentModelSupportsImages()}
                        className={`flex-shrink-0 px-3 py-3 transition-colors border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed ${
                            currentModelSupportsImages() 
                                ? 'text-gray-600 bg-gray-100 hover:bg-gray-200' 
                                : 'text-gray-400 bg-gray-50 cursor-not-allowed'
                        }`}
                        title={currentModelSupportsImages() 
                            ? "Upload images" 
                            : "Image uploads require a vision model. Please select a Qwen VL model to upload images."
                        }
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                    </button>
                    
                    <input
                        type="text"
                        value={inputValue}
                        onChange={(e) => setInputValue(e.target.value)}
                        onKeyPress={handleKeyPress}
                        placeholder="Type your message and press Enter..."
                        className="flex-1 p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        disabled={isLoading}
                    />
                    <button
                        onClick={handleSendMessage}
                        disabled={isLoading || !inputValue.trim()}
                        className="px-6 py-3 text-white transition-colors bg-blue-500 rounded-lg hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {isLoading ? (
                            <div className="w-5 h-5 border-2 border-white rounded-full border-t-transparent animate-spin"></div>
                        ) : (
                            'Send'
                        )}
                    </button>
                </div>
                <p className="mt-2 text-xs text-gray-500">
                    Press Enter to send • Shift+Enter for new line • Drag & drop images to attach • Using: {getModelDisplayName(selectedModel)} • Chat Mode: {mode.toUpperCase()} • Streaming: {useStreaming ? 'On' : 'Off'}
                    {uploadedImages.length > 0 && (
                        <span className="ml-2 text-blue-600">• {uploadedImages.length} image(s) attached</span>
                    )}
                </p>
            </div>
        </div>
    );
};

export default LLMChat;